import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCurrentUser } from '../redux/user';
import { clearCustomer, clearChildPractices } from '../redux/customer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './Navbar.css';
import logo from '../images/ontraq_healthfirst.png';
import constants from '../constants';
import AuthService from '../services/authorization';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import { selectIsAdminOrOwner } from '../redux/user';
import { useSessionLoading } from '../contexts/SessionLoadingContext';
import { UserAccessLevels } from '../enums';

export default function Navbar() {
  const Auth = new AuthService();
  const dispatch = useDispatch();
  const [practiceAnchorEl, setPracticeAnchorEl] = useState(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const practiceOpen = Boolean(practiceAnchorEl);
  const accountOpen = Boolean(accountAnchorEl);
  const currentUser = useSelector(selectCurrentUser);
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner);
  const location = useLocation();
  const navigate = useNavigate();
  const isCorporate = location.pathname.includes('/corp');
  const isPractice = location.pathname.includes('/practice') && !location.pathname.includes('/corp');
  const { createSession } = useSession();
  const { startSessionLoading, stopSessionLoading } = useSessionLoading();

  const handleChangeCustomer = async (customerNumber) => {
    handleClose();
    if (Auth.getCurrentCustomerNumber() === customerNumber) {
      return false;
    }

    startSessionLoading();

    try {
      Auth.setCurrentCustomerNumber(customerNumber);

      dispatch(clearCustomer())

      const { currentUser, customer } = await createSession();
      const hasCorporatePermission = [UserAccessLevels.Owner, UserAccessLevels.Admin].includes(currentUser.access_level);

      const path = customer.corporate_parent && hasCorporatePermission ? '/corp/dashboard' : '/practice/dashboard';

      navigate(path);
    } catch (error) {
      console.error('Error changing customer:', error);
      navigate('/support');
    } finally {
      stopSessionLoading();
    }
  };

  const handleClick = (event) => {
    if (event.target?.name === 'selectPractice') {
      setPracticeAnchorEl(event.currentTarget);
    } else if (event.target?.name === 'myAccount') {
      setAccountAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setPracticeAnchorEl(null);
    setAccountAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearCustomer());
    handleClose();
    Auth.logout();
  };

  const handleNavigation = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLinkAdditionalPractices = () => {
    handleClose();
    window.location.href = `${constants.LOGIN_URL}/add-customer`;
  };

  return (
    <div className="navbar-container">
      <a href={constants.HEALTHFIRST_WEB_URL}>
        <img src={logo} className="company-logo" alt="company logo" />
      </a>
      <div className="navbar-right-content">
        <span className="m-r-8">
          {currentUser?.name ? currentUser?.name.toUpperCase() : ''} {currentUser?.customer_number ? `(#${currentUser?.customer_number})` : ''}
        </span>
        <div className="dropdown">
          <Button
            id="practice-button"
            aria-controls={practiceOpen ? 'practice-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={practiceOpen ? 'true' : undefined}
            variant="nav"
            name="selectPractice"
            onClick={handleClick}
          >
            Select a Practice
            <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
          </Button>
          <Menu
            id="practice-menu"
            anchorEl={practiceAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            open={practiceOpen}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'practice-button'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            {currentUser?.available_customers?.length &&
              [...currentUser.available_customers]
                .sort((a, b) => b.name - a.name)
                .map((customer, i) => {
                  return (
                    <MenuItem variant="nav" key={i} onClick={() => handleChangeCustomer(customer.customer_number)}>
                      <span className="nav-button">
                        {currentUser.name ? customer.name.toUpperCase() : ''} ({customer.customer_number})
                      </span>
                    </MenuItem>
                  );
                })}
            <MenuItem onClick={handleLinkAdditionalPractices}>
              <span className="nav-button">
                <FontAwesomeIcon icon="plus" size="xs" /> Link Additional Practices
              </span>
            </MenuItem>
          </Menu>
        </div>
        <div className="dropdown">
          <Button
            id="account-button"
            aria-controls={accountOpen ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={accountOpen ? 'true' : undefined}
            variant="nav"
            name="myAccount"
            onClick={handleClick}
          >
            My Account
            <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
          </Button>
          <Menu
            id="account-menu"
            anchorEl={accountAnchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            open={accountOpen}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'account-button'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            {currentUser?.corporate_parent && isPractice && isAdminOrOwner && (
              <MenuItem onClick={() => handleNavigation('/corp/dashboard')}>
                <span className="nav-button">Switch to OnTraq Corporate</span>
              </MenuItem>
            )}
            {currentUser?.corporate_parent && isCorporate && (
              <MenuItem onClick={() => handleNavigation('/practice/dashboard')}>
                <span className="nav-button">Switch to OnTraq Practice</span>
              </MenuItem>
            )}
            {isAdminOrOwner && (
              <MenuItem onClick={() => handleNavigation('/practice/team-members')}>
                <span className="nav-button">Team Members</span>
              </MenuItem>
            )}
            {currentUser?.corporate_parent && <Divider />}
            <MenuItem onClick={() => handleNavigation('/practice/settings')}>
              <span className="nav-button">Settings</span>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <span className="nav-button">Sign Out</span>
            </MenuItem>
          </Menu>
        </div>
      </div>
    </div>
  );
}
